/*************************************************************************
* Copyright (c) 2025 Omnineura AI Technologies Pvt Limited.
* All rights reserved.
* This code is licensed under the 'Omnineura AI License' found in the LICENSE file.
* Unauthorized copying, sharing, or use of this code is prohibited.
* The intellectual and technical concepts are proprietary to Omnineura AI Technologies Pvt Limited.
* Author: surya@Omnineura.ai
* Date: 11-09-2025
*************************************************************************/
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getHomeDashboardApi,
  getAnalyticsDashboardApi,
  getTopDocumentsApi,
  getRecentActivityApi,
  getAnalyticsSummaryApi,
} from "../services/api";

// Home Dashboard
export const getHomeDashboard = createAsyncThunk(
  "analytics/getHomeDashboard",
  async (_, { rejectWithValue }) => {
    try {
      return await getHomeDashboardApi();
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Analytics Dashboard
export const getAnalyticsDashboard = createAsyncThunk(
  "analytics/getAnalyticsDashboard",
  async (days = 7, { rejectWithValue }) => {
    try {
      return await getAnalyticsDashboardApi(days);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Top Documents
export const getTopDocuments = createAsyncThunk(
  "analytics/getTopDocuments",
  async (limit = 5, { rejectWithValue }) => {
    try {
      return await getTopDocumentsApi(limit);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Recent Activity
export const getRecentActivity = createAsyncThunk(
  "analytics/getRecentActivity",
  async (limit = 5, { rejectWithValue }) => {
    try {
      return await getRecentActivityApi(limit);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Analytics Summary
export const getAnalyticsSummary = createAsyncThunk(
  "analytics/getAnalyticsSummary",
  async (_, { rejectWithValue }) => {
    try {
      return await getAnalyticsSummaryApi();
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const initialState = {
  homeDashboard: null,
  analyticsDashboard: null,
  topDocuments: [],
  recentActivity: [],
  analyticsSummary: null,
  loading: false,
  error: null,
};

const analyticsReducer = createSlice({
  name: "analytics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Home Dashboard
      .addCase(getHomeDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getHomeDashboard.fulfilled, (state, action) => {
        state.loading = false;
        console.log("Home payload", action.payload);
        state.homeDashboard = action.payload;
      })
      .addCase(getHomeDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Analytics Dashboard
      .addCase(getAnalyticsDashboard.fulfilled, (state, action) => {
        console.log("Analytics payload", action.payload);
        state.analyticsDashboard = action.payload;
      })

      // Top Documents
      .addCase(getTopDocuments.fulfilled, (state, action) => {
        state.topDocuments = action.payload;
      })

      // Recent Activity
      .addCase(getRecentActivity.fulfilled, (state, action) => {
        state.recentActivity = action.payload;
      })

      // Analytics Summary
      .addCase(getAnalyticsSummary.fulfilled, (state, action) => {
        state.analyticsSummary = action.payload;
      });
  },
});

export default analyticsReducer.reducer;